import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from '@/lib/store';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Cloud, Smartphone, AlertTriangle, CheckCircle2, Circle } from 'lucide-react';

export const DataMergeDialog = () => {
    const { mergeConflict, setMergeConflict } = useStore();
    const location = useLocation();
    const [choice, setChoice] = useState<'cloud' | 'local'>('cloud');
    const [options, setOptions] = useState({
        plan: true,
        settings: false,
        profile: false,
    });

    // Define app routes where this dialog is allowed to show
    const isAppRoute = ['/dashboard', '/schedule', '/progress', '/race', '/settings', '/admin', '/player', '/onboarding'].some(
        path => location.pathname === path || location.pathname.startsWith(path + '/')
    );

    if (!mergeConflict.active || !isAppRoute) return null;

    const handleConfirm = () => {
        if (choice === 'cloud') {
            mergeConflict.onKeepCloud();
        } else {
            mergeConflict.onKeepLocal(options);
        }
        // Close dialog
        setMergeConflict({ ...mergeConflict, active: false });
    };

    return (
        <Dialog open={mergeConflict.active} onOpenChange={() => { }}>
            <DialogContent className="sm:max-w-[500px]" onPointerDownOutside={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        We found a new plan on this device
                    </DialogTitle>
                    <DialogDescription>
                        You have a saved plan in your cloud account, but you also created a new plan on this device as a guest.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 space-y-4">
                    <div className="grid gap-3">
                        {/* Option 1: Keep Cloud (Recommended) */}
                        <div
                            onClick={() => setChoice('cloud')}
                            className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors ${choice === 'cloud' ? 'border-primary bg-primary/5' : 'border-border hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                        >
                            <div className={`mt-1 ${choice === 'cloud' ? 'text-primary' : 'text-slate-400'}`}>
                                {choice === 'cloud' ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                            </div>
                            <div className="grid gap-1.5">
                                <Label className="font-semibold text-base cursor-pointer">
                                    Keep Saved Profile Plan (Recommended)
                                </Label>
                                <p className="text-sm text-muted-foreground">
                                    Discard the new plan on this device and continue with your saved cloud data.
                                    You can create a new plan anytime via the Quick Start guide or Settings → Training.
                                </p>
                                <div className="text-xs bg-muted p-2 rounded mt-1 flex gap-2 items-center text-muted-foreground w-fit">
                                    <Cloud className="h-3 w-3" />
                                    <span>{mergeConflict.cloudSummary || 'Existing Cloud Data'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Option 2: Keep Local */}
                        <div
                            onClick={() => setChoice('local')}
                            className={`flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors ${choice === 'local' ? 'border-primary bg-primary/5' : 'border-border hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                        >
                            <div className={`mt-1 ${choice === 'local' ? 'text-primary' : 'text-slate-400'}`}>
                                {choice === 'local' ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                            </div>
                            <div className="grid gap-1.5 w-full">
                                <Label className="font-semibold text-base cursor-pointer">
                                    Keep New Onboarding Plan
                                </Label>
                                <p className="text-sm text-muted-foreground">
                                    Overwrite your saved cloud plan with this new one.
                                </p>
                                <div className="text-xs bg-muted p-2 rounded mt-1 flex gap-2 items-center text-muted-foreground w-fit">
                                    <Smartphone className="h-3 w-3" />
                                    <span>{mergeConflict.localSummary || 'New Local Plan'}</span>
                                </div>

                                {/* Granular Checkboxes (Only visible if 'local' is selected) */}
                                {choice === 'local' && (
                                    <div className="mt-4 space-y-3 pl-1 border-t pt-3 animate-in fade-in slide-in-from-top-2 duration-200" onClick={(e) => e.stopPropagation()}>
                                        <p className="text-xs font-medium uppercase text-muted-foreground mb-2">What to overwrite?</p>

                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="opt-plan"
                                                checked={options.plan}
                                                onCheckedChange={(c) => setOptions({ ...options, plan: c as boolean })}
                                            />
                                            <label htmlFor="opt-plan" className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                                Training Plan & Schedule
                                            </label>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="opt-profile"
                                                checked={options.profile}
                                                onCheckedChange={(c) => setOptions({ ...options, profile: c as boolean })}
                                            />
                                            <label htmlFor="opt-profile" className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                                Profile Settings (HR Zones, Weight)
                                            </label>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="opt-settings"
                                                checked={options.settings}
                                                onCheckedChange={(c) => setOptions({ ...options, settings: c as boolean })}
                                            />
                                            <label htmlFor="opt-settings" className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                                App Preferences
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter className="sm:justify-between">
                    <p className="text-xs text-muted-foreground self-center hidden sm:block">
                        {choice === 'cloud'
                            ? 'Your new local plan will be lost forever.'
                            : 'This action cannot be undone.'}
                    </p>
                    <Button onClick={handleConfirm} variant={choice === 'cloud' ? 'default' : 'destructive'}>
                        {choice === 'cloud' ? 'Discard Local & Load Cloud' : 'Overwrite Cloud & Keep Local'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
